import { HttpClient, HttpHeaders } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';
import { catchError, map, Observable, of, timeout, TimeoutError } from 'rxjs';
import { BASE_URL, HEADERS_API } from '../../app.constants';
import { LoginRequest } from '../interfaces/request/login-request';
import { LoginResponse } from '../interfaces/response/login-response';
import { LocalStorageKey } from '../../shared/enums/local-storage.enum';
import { SessionClass } from '../class/session-class';
import {
  ERROR_MESSAGE,
  SUCCESS_MESSAGE,
} from '../../shared/constants/error-messages.constants';
import { MfaLoginResponse } from '../interfaces/response/mfa-login-response';
import { GenericResponse } from '../interfaces/response/generic-response';
import { VerifyMFARequest } from '../interfaces/request/verify-mfa-request';
import { AlertService } from '../../shared/services/alert.service';
import { LoadingService } from '../../shared/services/loading.service';
import { User } from '../interfaces/user-interface';
import { RecoveryPass } from '../interfaces/request/recovery-pass-request';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private _authStatus = signal<boolean>(false);
  private _session = signal<string | null>(
    localStorage.getItem(LocalStorageKey.Session)
  );

  private http = inject(HttpClient);
  private alertService = inject(AlertService);
  private loadingService = inject(LoadingService);
  private router = inject(Router);
  private _expiryWatcherId: number | null = null;
  private isRedirectingToLogin = false;

  // Emits when logout is performed so components can cleanup
  private _logoutSubject = new Subject<void>();
  logout$ = this._logoutSubject.asObservable();

  authStatus = computed<boolean>(() => {
    return this._authStatus();
  });

  constructor() {
    this.startExpiryWatcher();
    this.startVisibilityWatcher();
  }

  private startExpiryWatcher() {
    try {
      if (this._expiryWatcherId) {
        clearInterval(this._expiryWatcherId);
      }
      this._expiryWatcherId = window.setInterval(() => {
        this.checkExpiryAndRedirect();
      }, 60 * 1000);
    } catch (e) {
      console.warn('No se pudo iniciar expiry watcher', e);
    }
  }

  // Revalida la sesión cuando el usuario vuelve a la pestaña/app,
  // ya que los timers se pausan mientras está en segundo plano.
  private startVisibilityWatcher() {
    try {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.checkExpiryAndRedirect();
        }
      });
      window.addEventListener('focus', () => {
        this.checkExpiryAndRedirect();
      });
    } catch (e) {
      console.warn('No se pudo iniciar visibility watcher', e);
    }
  }

  private checkExpiryAndRedirect() {
    const expiryStr = localStorage.getItem(LocalStorageKey.SessionExpiry);
    if (!expiryStr) {
      return;
    }
    const expiry = parseInt(expiryStr, 10);
    if (!isNaN(expiry) && Date.now() > expiry) {
      this.handleInvalidSession();
    }
  }

  handleInvalidSession(): void {
    if (this.isRedirectingToLogin) {
      return;
    }

    this.isRedirectingToLogin = true;
    this.logout();
    try {
      void this.router.navigateByUrl('/auth/login').finally(() => {
        this.isRedirectingToLogin = false;
      });
    } catch (e) {
      // ignore navigation errors
      this.isRedirectingToLogin = false;
    }
  }

  login(loginRequest: LoginRequest): Observable<MfaLoginResponse | null> {
    this.loadingService.show();
    return this.http
      .post<MfaLoginResponse>(`${BASE_URL}controller/mfa/login`, loginRequest, {
        headers: HEADERS_API,
      })
      .pipe(
        timeout(7000),
        map((response) => {
          this.loadingService.hide();
          if (response.codigo == 0) {
            return response;
          } else {
            return this.handleAuthError(null, response.mensaje);
          }
        }),
        catchError((error: any) => {
          this.loadingService.hide();
          return of(this.handleAuthError(error, ERROR_MESSAGE.NETWORK_ERROR));
        })
      );
  }

  verifyCodeMfa(verifyMfaRequest: VerifyMFARequest): Observable<boolean> {
    this.loadingService.show();
    return this.http
      .post<LoginResponse>(
        `${BASE_URL}controller/mfa/verify`,
        verifyMfaRequest,
        {
          headers: HEADERS_API,
        }
      )
      .pipe(
        timeout(7000),
        map((response) => {
          this.loadingService.hide();
          if (response.codigo == 200) {
            return this.handleAuthSuccess(response);
          } else {
            this.handleAuthError(null, response.mensaje);
            return false;
          }
        }),
        catchError((error: any) => {
          this.loadingService.hide();
          this.handleAuthError(error, ERROR_MESSAGE.NETWORK_ERROR);

          return of(false);
        })
      );
  }

  recoveryPassword(
    recoveryPass: RecoveryPass
  ): Observable<GenericResponse | null> {
    this.loadingService.show();
    return this.http
      .post<LoginResponse>(`${BASE_URL}Auth/resetPass`, recoveryPass, {
        headers: HEADERS_API,
      })
      .pipe(
        timeout(7000),
        map((response) => {
          this.loadingService.hide();
          if (response.codigo == 0) {
            this.alertService.showAlert(
              SUCCESS_MESSAGE.RECOVERY_SUCCESS,
              'success',
              false
            );
            return response;
          } else {
            return this.handleAuthError(null, response.mensaje);
          }
        }),
        catchError((error: any) => {
          this.loadingService.hide();
          return of(this.handleAuthError(error, ERROR_MESSAGE.NETWORK_ERROR));
        })
      );
  }

  private handleAuthSuccess(response: LoginResponse): boolean {
    const session = new SessionClass(
      response.token,
      response.sesionId,
      response.user
    );

    const sessionJson = JSON.stringify(session);

    this._session.set(sessionJson);
    this._authStatus.set(true);

    localStorage.setItem(
      LocalStorageKey.Session,
      sessionJson
    );

    // Guardar timestamp de expiración de sesión 
    try {
      const now = Date.now();
      const fourHoursMs = 4 * 60 * 60 * 1000; // 4 horas
      const expiry = (now + fourHoursMs).toString();
      localStorage.setItem(LocalStorageKey.SessionExpiry, expiry);
    } catch (e) {
      console.warn('No se pudo guardar expiry en localStorage', e);
    }

    // nueva sesión válida: permitir futuras redirecciones por sesión inválida
    this.isRedirectingToLogin = false;

    return true;
  }


  private handleAuthError(error: any, messageError: string) {
    this.logout();
    this.alertService.showAlert(messageError, 'danger', false);
    return null;
  }

  logout() {
    this._session.set(null);
    this._authStatus.set(false);
    localStorage.clear();
    try {
      this._logoutSubject.next();
    } catch (e) {
      // ignore
    }
  }

  isLoggedIn(): boolean {
    try {
      const localSession = localStorage.getItem(
        LocalStorageKey.Session
      );

      if (!localSession) {
        return false;
      }

      const jsonSession: SessionClass = JSON.parse(localSession);

      // Verificar expiración almacenada
      const expiryStr = localStorage.getItem(LocalStorageKey.SessionExpiry);
      if (expiryStr) {
        const expiry = parseInt(expiryStr, 10);
        if (!isNaN(expiry) && Date.now() > expiry) {
          // expiró la sesión
          this.logout();
          return false;
        }
      }

      return (
        jsonSession.token !== null &&
        jsonSession.token !== undefined &&
        jsonSession.token !== '' &&
        jsonSession.sessionId !== null &&
        jsonSession.sessionId !== undefined &&
        jsonSession.user !== null &&
        jsonSession.user !== undefined
      );

    } catch (error) {
      console.error('Error al validar sesión:', error);
      return false;
    }
  }


  private notNullOrUndefined(value: any): boolean {
    return value !== null && value !== undefined;
  }

  getUser(): User | null {
    let user: User | null;
    if (localStorage.getItem(LocalStorageKey.Session)) {
      try {
        user = JSON.parse(localStorage.getItem(LocalStorageKey.Session)!).user;
        return user;
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  getToken(): string {
    let token: string;
    if (localStorage.getItem(LocalStorageKey.Session)) {
      try {
        token = JSON.parse(
          localStorage.getItem(LocalStorageKey.Session)!
        ).token;
        return token;
      } catch (e) {
        return 'null';
      }
    }
    return 'null';
  }
}
